// anime-ui.js — micro-animaciones modernas con anime.js v4 para las apps del grupo.
// Origen: skill animejs-ui-recipes. Todas respetan prefers-reduced-motion (saltan al estado final).
import { animate, stagger, createSpring, splitText, scrambleText, onScroll, utils } from "animejs"

const RM = () =>
  typeof window !== "undefined" && window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

/* ───────────────── TARJETAS ───────────────── */

// Entrada al montar: las tarjetas suben + aparecen en cascada con muelle.
export function revealCards(targets, { delay = 70, from = "first", y = 18 } = {}) {
  if (RM()) { utils.set(targets, { opacity: 1 }); return }
  utils.set(targets, { opacity: 0 })
  return animate(targets, {
    opacity: [0, 1], translateY: [y, 0], scale: [0.96, 1],
    delay: stagger(delay, { from }), duration: 620,
    ease: createSpring({ stiffness: 90, damping: 12 }),
  })
}

// Igual pero disparada cuando la sección entra en pantalla (ScrollObserver de anime.js).
export function revealOnScroll(targets, { delay = 80, container } = {}) {
  if (RM()) { utils.set(targets, { opacity: 1 }); return }
  utils.set(targets, { opacity: 0 })
  return animate(targets, {
    opacity: [0, 1], translateY: [24, 0],
    delay: stagger(delay), duration: 600, ease: "out(3)",
    autoplay: onScroll({ target: container || targets, enter: "bottom-=60 top" }),
  })
}

// Inclinación 3D + elevación al pasar el ratón. Devuelve cleanup.
export function cardHover(el, { max = 8, lift = -6, scale = 1.03 } = {}) {
  if (!el || RM()) return () => {}
  const move = (e) => {
    const r = el.getBoundingClientRect()
    const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height
    el.style.transition = "transform .08s linear"
    el.style.transform =
      `perspective(720px) rotateX(${-(py - .5) * 2 * max}deg) rotateY(${(px - .5) * 2 * max}deg) translateY(${lift}px) scale(${scale})`
  }
  const leave = () => {
    el.style.transition = "transform .5s cubic-bezier(.22,.61,.36,1)"
    el.style.transform = "perspective(720px) rotateX(0) rotateY(0) translateY(0) scale(1)"
  }
  el.addEventListener("mousemove", move); el.addEventListener("mouseleave", leave)
  return () => { el.removeEventListener("mousemove", move); el.removeEventListener("mouseleave", leave) }
}

// Rebote al pulsar (feedback táctil). Devuelve cleanup.
export function cardPress(el) {
  if (!el) return () => {}
  const down = () => animate(el, { scale: 0.97, duration: 120, ease: "out(2)" })
  const up = () => animate(el, { scale: 1, ease: createSpring({ stiffness: 140, damping: 10 }) })
  el.addEventListener("pointerdown", down)
  el.addEventListener("pointerup", up)
  el.addEventListener("pointerleave", up)
  return () => { el.removeEventListener("pointerdown", down); el.removeEventListener("pointerup", up); el.removeEventListener("pointerleave", up) }
}

/* ───────────────── TEXTO ───────────────── */

// Revela un titular palabra a palabra (o "chars"/"lines") con muelle.
export function splitReveal(target, { by = "words", delay = 40, y = 18, spring = true } = {}) {
  if (RM()) return
  let parts
  try { parts = splitText(target, { [by]: true })[by] } catch (e) { return }
  utils.set(parts, { opacity: 0 })
  return animate(parts, {
    opacity: [0, 1], translateY: [y, 0],
    delay: stagger(delay), duration: 700,
    ease: spring ? createSpring({ stiffness: 100, damping: 12 }) : "out(3)",
  })
}

// Cuenta un número de `from` a `to` (KPIs, bandejas, dashboards).
export function countUp(el, to, { duration = 1400, ease = "out(3)", decimals = 0, prefix = "", suffix = "", from = 0 } = {}) {
  if (!el) return
  const fmt = (n) => prefix + Number(n).toLocaleString("es-ES", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix
  if (RM()) { el.textContent = fmt(to); return }
  const o = { v: from }
  return animate(o, { v: to, duration, ease, onUpdate: () => { el.textContent = fmt(o.v) } })
}

// Revela texto "descifrándolo" (efecto terminal). Usa el texto actual del elemento como destino.
export function scrambleIn(el, { chars = "uppercase", duration = 1600, ease = "inOut(2)" } = {}) {
  if (!el || RM()) return
  return animate(el, { text: scrambleText({ chars }), duration, ease })
}

/* ───────────────── DATOS / INFO DE APPS ───────────────── */

// Filas de tabla/lista entrando en cascada (Estado Pedidos, logs, etc.).
export function staggerRows(targets, { delay = 45, y = 10 } = {}) {
  if (RM()) { utils.set(targets, { opacity: 1 }); return }
  utils.set(targets, { opacity: 0 })
  return animate(targets, {
    opacity: [0, 1], translateY: [y, 0],
    delay: stagger(delay), duration: 460, ease: "out(3)",
  })
}

// Pulso de un punto/badge de estado (cambio de estado en Monitor POWER).
export function pulse(el, { scale = 1.45, duration = 620, loop = false } = {}) {
  if (!el || RM()) return
  return animate(el, { scale: [1, scale, 1], opacity: [1, .7, 1], duration, ease: "out(3)", loop })
}

// Anima una barra de progreso a `pct` (% de plazo, avance, etc.).
export function progressTo(el, pct, { duration = 900, ease = "inOutQuad" } = {}) {
  if (!el) return
  return animate(el, { width: pct + "%", duration: RM() ? 0 : duration, ease })
}

// Destello breve cuando un dato se actualiza (ideal para datos en vivo).
export function flashUpdate(el, { color = "rgba(0,81,135,.18)", duration = 750 } = {}) {
  if (!el || RM()) return
  return animate(el, { backgroundColor: [color, "rgba(0,81,135,0)"], duration, ease: "out(3)" })
}

// Notificación deslizante (esquina inferior derecha) con auto-cierre.
export function toast(message, { duration = 3200, accent = "#005187" } = {}) {
  if (typeof document === "undefined") return
  let host = document.getElementById("anime-toast-host")
  if (!host) {
    host = document.createElement("div"); host.id = "anime-toast-host"
    host.style.cssText = "position:fixed;right:18px;bottom:18px;z-index:99999;display:flex;flex-direction:column;gap:8px;pointer-events:none"
    document.body.appendChild(host)
  }
  const t = document.createElement("div"); t.textContent = message
  t.style.cssText = `pointer-events:auto;font-family:'Plus Jakarta Sans',system-ui,sans-serif;font-size:13px;font-weight:600;color:#0f172a;background:#fff;border:1px solid #E5E7EB;border-left:3px solid ${accent};border-radius:10px;padding:10px 14px;box-shadow:0 10px 30px rgba(8,40,70,.16)`
  host.appendChild(t)
  if (RM()) { setTimeout(() => t.remove(), duration); return t }
  animate(t, { opacity: [0, 1], translateX: [40, 0], ease: createSpring({ stiffness: 120, damping: 14 }) })
  setTimeout(() => animate(t, { opacity: 0, translateX: 40, duration: 300, onComplete: () => t.remove() }), duration)
  return t
}
